import { useRef, useEffect, useState } from "react";
import "./SearchTable.css";
import HighlightedText from "../../../components/HighlightKeyword";
import download from "downloadjs";
import api from "../../../services/baseUrl";
import Modal from "../../eGazette/search/Modal";
import CustomModal from "../DetailModal";
const SearchTable = ({
  tableIndex,
  activeDropdownIndex,
  setActiveDropdownIndex,
  caseLawDocument,
  query,
}) => {
  const dropdownRef = useRef(null);
  const isDropdownOpen = activeDropdownIndex === tableIndex;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [documentId, setDocumentId] = useState("");

  const openModalWithContent = (content, title) => {
    setModalContent(content);
    setIsDetailModalOpen(true);
    setModalTitle(title);
  };
  const [file, setFile] = useState(null);
  const [fileUUID, setFileUUID] = useState("");
  const openModal = async (uuid) => {
    if (uuid) {
      setFileUUID(uuid);
      fetchPdf(uuid);
      setIsModalOpen(true);
    }
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setFile(null);
    setDocumentId("");
  };

  const toggleDropdown = () => {
    setActiveDropdownIndex(isDropdownOpen ? null : tableIndex); // Open new, close if same
  };

  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (dropdownRect.bottom > viewportHeight) {
        dropdownRef.current.classList.add("open-up");
      } else {
        dropdownRef.current.classList.remove("open-up");
      }
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setActiveDropdownIndex(null);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isDropdownOpen) {
            setActiveDropdownIndex(null);
          }
        });
      },
      {
        root: null,
        threshold: 0,
      }
    );

    const currentRef = dropdownRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isDropdownOpen, setActiveDropdownIndex]);

  const fetchCaseLawPdf = async (documentID, pageNumber) => {
    try {
      const response = await api.get("/api/caselaw-documents/fileUUID", {
        params: { documentID: documentID },
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (pageNumber) {
        setPageNumber(pageNumber);
      } else {
        setPageNumber(1);
      }
      setFileUUID(response.data);
      openModal(response.data);
    } catch (error) {
      console.error("Error fetching PDF:", error);
    }
  };

  const fetchPdf = async (uuid) => {
    try {
      const response = await api.post(
        "/document/pdf",
        { fileUUID: uuid },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const pdfData = response.data.pdfData;

      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      setFile(url);
    } catch (error) {
      console.error("Error fetching PDF:", error);
    }
  };

  const downloadPdf = async (uuid) => {
    try {
      const fileNameEndpoint = "/api/caselaw-documents/getFileName";
      const fileNameResponse = await api.post(
        fileNameEndpoint,
        { fileUUID: uuid },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const fileNameText = await fileNameResponse.data;
      const fileName = fileNameText.split("/").pop();

      const response = await api.post(
        "/document/pdf/download",
        { fileUUID: uuid },
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const contentType = response.headers["content-type"];
      download(response.data, fileName, contentType);
    } catch (error) {
      console.error("Error downloading the PDF:", error);
    }
  };
  return (
    <>
      <table className="caselaw-result-table">
        <tr>
          <td colSpan="4">
            <div
              className="result-detail result-case-title"
              style={{ fontWeight: "bold" }}
            >
              {caseLawDocument?.metadata?.[0]?.caseTitle
                ?.replace(
                  /\w\S*/g,
                  (txt) =>
                    txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
                )
                .toUpperCase()}
            </div>
            <br />
            <br />
            <div className="result-detail result-case-title">
              {caseLawDocument?.chunks?.map((chunk, index) => (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    fetchCaseLawPdf(
                      chunk?.metadata?.[0]?.documentID,
                      chunk?.pageNumber
                    );
                    setDocumentId(chunk?.metadata?.[0]?.documentID);
                  }}
                >
                  <HighlightedText
                    keyword={query}
                    paragraph={chunk.relevant_excerpt}
                    onClick={() => {}}
                  />
                  <br />
                </div>
              ))}
            </div>
          </td>
        </tr>
        <tr>
          <td width="28%">
            <div className="result-detail-container">
              <div className="result-detail">
                <span>Citation:</span>{" "}
                {caseLawDocument?.metadata?.[0]?.citation}
              </div>
              <div className="result-detail">
                <span>Case No:</span> {caseLawDocument?.metadata?.[0]?.caseNo}
              </div>
            </div>
          </td>
          <td width="20%">
            <div className="result-detail-container">
              <div className="result-detail">
                <span>Date of judgement:</span>{" "}
                {new Date(
                  caseLawDocument?.metadata?.[0]?.decisionDate
                ).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </div>
            </div>
          </td>
          <td width="18%">
            <div className="result-detail-container">
              <div className="result-detail">
                <span>Court:</span> {caseLawDocument?.metadata?.[0]?.courtName}
              </div>
              <div className="result-detail">
                <span>Location:</span>{" "}
                {caseLawDocument?.metadata?.[0]?.courtLocation}
              </div>
            </div>
          </td>
          <td width="15%">
            <div className="dropdown-wrapper">
              <button className="dropdown-button" onClick={toggleDropdown}>
                Discover More ▼
              </button>
              {isDropdownOpen && (
                <ul ref={dropdownRef} className="dropdown-menu">
                  <li
                    onClick={() => {
                      fetchCaseLawPdf(
                        caseLawDocument?.metadata?.[0]?.documentID
                      );
                    }}
                  >
                    View & Download Case (PDF)
                  </li>
                  <li
                    onClick={() => {
                      console.log(caseLawDocument?.metadata?.[0]?.judgeName);
                      
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.judgeName.toUpperCase(),
                        "Judges"
                      );
                    }}
                  >
                    Judges
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.summaryFacts,
                        "Summary"
                      );
                    }}
                  >
                    Summary
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.casesReferredTo,
                        "Cases Referred"
                      );
                    }}
                  >
                    Cases Referred
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.legislationReferredTo,
                        "Legislation Referred To"
                      );
                    }}
                  >
                    Legislation Referred
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.otherResources,
                        "Other Reference"
                      );
                    }}
                  >
                    Other Reference
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.keyword,
                        "Subject Matter"
                      );
                    }}
                  >
                    Subject Matter
                  </li>
                  <li
                    onClick={() => {
                      openModalWithContent(
                        caseLawDocument?.metadata?.[0]?.caseName,
                        "Type of Case"
                      );
                    }}
                  >
                    Type of Case
                  </li>
                </ul>
              )}
            </div>
          </td>
        </tr>
      </table>
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        keyword={query}
        pdf={file}
        pageNumber={pageNumber}
        fileUUID={fileUUID}
        downloadPdf={(uuid) => downloadPdf(uuid)}
        documentId={documentId}
      />
      <CustomModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        content={modalContent}
        title={modalTitle}
      />
    </>
  );
};

export default SearchTable;
